import { Mail, Phone, Xmark } from 'iconoir-react'
import { AnimatePresence, m } from 'motion/react'
import { useEffect, useRef, useState } from 'react'

// Desktop-only floating action button (mobile already has the fixed call
// bar doing this job). Click reveals two direct contact options -- no
// chat widget/backend, just a shortcut to the phone and email that are
// already in the footer.
export function FloatingContact() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={ref} className="fixed bottom-8 right-8 z-40 hidden flex-col items-end gap-3 min-[900px]:flex">
      <AnimatePresence>
        {open && (
          <m.div
            initial={{ opacity: 0, y: 12, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.95 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col gap-1 rounded-2xl border border-n-200 bg-n-300 p-2"
          >
            <a
              href="tel:+19568871317"
              data-cursor="link"
              className="flex items-center gap-3 rounded-xl px-4 py-3 text-body text-ink transition-colors hover:bg-n-400"
            >
              <Phone width={18} height={18} className="text-flow" />
              (956) 887-1317
            </a>
            <a
              href="mailto:FlowScale1@outlook.com"
              data-cursor="link"
              className="flex items-center gap-3 rounded-xl px-4 py-3 text-body text-ink transition-colors hover:bg-n-400"
            >
              <Mail width={18} height={18} className="text-flow" />
              Email us
            </a>
          </m.div>
        )}
      </AnimatePresence>
      <m.button
        type="button"
        data-cursor="link"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close contact options' : 'Contact us'}
        aria-expanded={open}
        whileTap={{ scale: 0.95 }}
        className="flex h-14 w-14 items-center justify-center rounded-full bg-flow text-n-100 transition-colors hover:bg-flow-hi"
      >
        <m.span animate={{ rotate: open ? 45 : 0 }} transition={{ duration: 0.2 }}>
          {open ? <Xmark width={22} height={22} /> : <Mail width={22} height={22} />}
        </m.span>
      </m.button>
    </div>
  )
}
